/**
 * Notifications Service
 * Flutter source: lib/data/data_sources/messages_data_source.dart:7-44
 *
 * Thin domain fetch+remap path for a page of notifications: calls the
 * Firestore collection-group query (`firestoreService.getNotifications`) and
 * maps the raw page JSON to `AppNotificationEntity[]` via `AppNotificationRemapper`.
 *
 * Mirrors Flutter where the data source returns the raw page
 * (`{ notifications, hasMore, lastFetchedItem }`) with NO filtering. The
 * notification-type filter (`contentAdded` / `earlyAccessContentAdded` /
 * `liveStarted` dropped), the `addAllUnique` dedup, and any unseen-badge logic
 * happen in the SCREEN (web: the hook, Task 8) — see
 * lib/presentation/screens/messages/messages_screen.dart:172-191 — and are
 * deliberately NOT in this service.
 */
import type { Unsubscribe } from 'firebase/firestore';
import { firestoreService } from '@/data/services/firestoreService';
import type { FirestoreService } from '@/data/services/firestoreService';
import { AppNotificationRemapper } from '@/domain/remappers/notification/appNotificationRemapper';
import type { AppNotificationEntity } from '@/domain/entities/notification/appNotificationEntity';
import type { RemoteDocumentChangeType } from '@/data/types/remoteDocumentChangeType';
import type { PageCursor } from '@/data/types/json';
import { appLogger } from '@/domain/services/analytics/appLogger';

export class NotificationsService {
  private static instance: NotificationsService;
  private firestoreServiceInstance: FirestoreService;

  constructor(firestoreServiceInstance: FirestoreService = firestoreService) {
    this.firestoreServiceInstance = firestoreServiceInstance;
  }

  /**
   * Singleton accessor. Use cases should default to this instead of
   * constructing a fresh service. The public constructor is retained for test
   * injection of a stub `FirestoreService`.
   */
  static getInstance(): NotificationsService {
    if (!this.instance) {
      this.instance = new NotificationsService();
    }
    return this.instance;
  }

  /**
   * Fetch a paginated page of notifications for `owner`, newest-first.
   *
   * Thin fetch+remap: returns the raw page mapped to entities. NO type filter,
   * NO dedup, NO badge logic — those belong to the hook (Task 8).
   */
  async fetchNotifications(
    owner: string,
    cursor: PageCursor | undefined,
    limit: number,
  ): Promise<{ notifications: AppNotificationEntity[]; hasMore: boolean; cursor?: PageCursor }> {
    try {
      const response = await this.firestoreServiceInstance.getNotifications(
        owner,
        cursor,
        limit,
      );

      const notifications = AppNotificationRemapper.fromDtoList(
        AppNotificationRemapper.fromJsonList(response.items),
      );

      return {
        notifications,
        hasMore: response.hasMore,
        cursor: response.cursor,
      };
    } catch (error) {
      appLogger.error(
        'Error fetching notifications',
        'NotificationsService.fetchNotifications',
        { error: error as Error },
      );
      return { notifications: [], hasMore: false };
    }
  }

  /**
   * Mark a single notification as read.
   *
   * Thin pass-through onto the data-layer write
   * (`firestoreService.setNotificationRead`), which issues the dotted
   * `{'info.read': true}` field update on
   * `users/{ownerUserId}/notifications/{notificationId}`.
   *
   * Mirrors Flutter `NotificationsRepository.setNotificationRead`
   * (lib/data/repository/notifications_repository.dart:16-18), which simply
   * forwards to `NotificationsDataSource().setNotificationRead(notification)`
   * (lib/data/data_sources/notifications_data_source.dart:5-12). Flutter passes
   * the whole `AppNotification` and the data source destructures
   * `info.usersInfo.peerUserId` + `id`; the web splits it the same way —
   * entity in at the use case, primitives at the data layer.
   *
   * The `if (!notification.info.read)` gate is the CALLER's
   * (lib/services/push_navigation_service.dart:357-359) — on web that is the
   * inbox feature hook's `markNotificationRead`, never this service.
   *
   * @param ownerUserId - The notification recipient (`info.usersInfo.peerUserId`).
   * @param notificationId - The notification document id.
   * @returns `true` when the write succeeded, `false` when it threw (logged).
   */
  async setNotificationRead(ownerUserId: string, notificationId: string): Promise<boolean> {
    try {
      await this.firestoreServiceInstance.setNotificationRead(ownerUserId, notificationId);
      return true;
    } catch (error) {
      appLogger.error(
        'Error setting notification read',
        'NotificationsService.setNotificationRead',
        { error: error as Error },
      );
      return false;
    }
  }

  /**
   * Subscribe to live notification changes for `userId` arriving at or after
   * `sinceTimestamp`, remapping each raw Firestore change into an
   * `AppNotificationEntity` before delivery so the consumer never sees raw JSON.
   *
   * Thin subscribe+remap proxy — mirrors `ChatService.subscribeToChatNodes`.
   * Calls the data-layer collection-group snapshot
   * (`firestoreService.subscribeToNotifications`) and maps each raw change via
   * `AppNotificationRemapper.fromDto(fromJson(...))`. Unlike the chat-node
   * subscription there is deliberately NO decryption / peer-resolve step:
   * notifications carry no encrypted body.
   *
   * Matches Flutter: lib/presentation/screens/messages/messages_screen.dart:323-344
   * (`_subscribeToNotificationsUpdates`; each change parsed at lines 342-344 via
   * `AppNotification.fromJson(change.doc.data())`). The three dropped notification
   * types (`contentAdded` / `earlyAccessContentAdded` / `liveStarted`), the
   * `>=`-window cursor math, and the unread-badge re-check live downstream in the
   * use case (Task 3) and the hook (Tasks 4-5) — NOT here. `sinceTimestamp` is
   * forwarded UNCHANGED to the data layer.
   *
   * @param userId - The current user's Firebase id (the `info.usersInfo.peerUserId` match).
   * @param sinceTimestamp - The `>=` cursor on `info.timestamp`, resolved by the
   *   caller (the most-recent first-page notification timestamp, or `now()` when
   *   the first page was empty). Forwarded verbatim to the data layer.
   * @param onChange - Called once per snapshot batch with the array of changes.
   *   Each change carries an `AppNotificationEntity` produced by
   *   `AppNotificationRemapper.fromDto(fromJson(...))`.
   * @param onError - Optional error sink for the underlying snapshot listener.
   * @returns An `Unsubscribe` handle the caller is responsible for invoking.
   */
  subscribeToNotifications(
    userId: string,
    sinceTimestamp: number,
    onChange: (
      changes: Array<{ type: RemoteDocumentChangeType; data: AppNotificationEntity }>,
    ) => void,
    onError?: (error: Error) => void,
  ): Unsubscribe {
    return this.firestoreServiceInstance.subscribeToNotifications(
      userId,
      sinceTimestamp,
      (rawChanges) => {
        const changes = rawChanges.map((c) => ({
          type: c.type,
          data: AppNotificationRemapper.fromDto(AppNotificationRemapper.fromJson(c.data)),
        }));
        onChange(changes);
      },
      onError,
    );
  }
}

// Export singleton instance for convenience
export const notificationsService = NotificationsService.getInstance();
